<template>
  <InstanceSyncModal
    :open="showInstanceSyncModal"
    :close="handleCloseInstanceSyncModal"
    :componentId="componentId"
    :instanceName="instanceName"
    :formData="formData"
    type="component"
  />
  <DraftPublishConfirmation
    :open="showDraftPublicConfirmation"
    :close="handleCloseDraftPublishModal"
    :handlePublish="handlePublish"
  />
  <InstanceNameEditModal
    :open="showNameEditModal"
    :close="handleCloseNameEditModal"
    :componentId="componentId"
    :draftId="draftId"
    :instanceId="instanceId"
  />
  <ComponentPreviewModal
    :open="showComponentPreviewModal"
    :close="handleCloseComponentPreviewModal"
    v-if="draftModeStatusLocal === 'yes'"
    :draft="`draft`"
    :shortCode="`[${this.componentName
      .toLowerCase()
      .replaceAll(' ', '_')} name=${this.componentId}|${this.instanceName}]`"
  />
  <ComponentPreviewModal
    :open="showComponentPreviewModal"
    :close="handleCloseComponentPreviewModal"
    v-else
    :shortCode="`[${this.componentName
      .toLowerCase()
      .replaceAll(' ', '_')} name=${this.componentId}|${this.instanceName}]`"
  />
  <div
    class="rounded-t bg-white mb-0 px-6 py-6 flex justify-between uppercase font-bold items-center"
  >
    <div class="flex">
      <button @click="handleGoBack" class="flex">
        <IconChevronLeft />{{ formattedTitle(title) || "Loading..." }}
      </button>
      <div v-if="hasDraft" class="flex items-center justify-center">
        <ToggleBtn
          class="ml-4"
          :status="draftModeStatusLocal"
          @update:status="handleUpdateStatus($event)"
        />
        <transition
          enter-active-class="duration-300 ease-out"
          enter-from-class="transform opacity-0"
          enter-to-class="opacity-100"
          leave-active-class="duration-200 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="transform opacity-0"
          mode="out-in"
        >
          <div
            v-if="draftModeStatusLocal === 'yes'"
            class="ml-2 bg-emerald-200 py-1 px-2 rounded-full select-none"
          >
            <p class="text-sm">Draft</p>
          </div>
          <div
            v-else
            class="ml-2 bg-yellow-200 py-1 px-2 rounded-full select-none"
          >
            <p class="text-sm">Live</p>
          </div></transition
        >
      </div>
    </div>
    <transition
      enter-active-class="duration-300 ease-out"
      enter-from-class="transform opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="transform opacity-0"
      mode="out-in"
    >
      <!-- *** show instance operation buttons when user select instance edit mode -->
      <div
        v-if="draftModeStatusLocal === 'no'"
        class="text-center flex justify-end items-center"
      >
        <button
          @click="handleCreateDraft"
          :disabled="!hasInstance"
          v-if="!hasDraft"
          :class="
            hasInstance
              ? 'bg-emerald-500 active:bg-emerald-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          class="inline-flex bg-emerald-500 active:bg-emerald-600 inline-flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          Create Draft
          <IconSync class="animate-spin" v-if="getDraftCreateLoading" />
        </button>
        <button
          :disabled="!hasInstance"
          @click="handleOpenInstanceSyncModal"
          :class="
            hasInstance
              ? 'bg-emerald-500 active:bg-emerald-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          class="flex text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          <IconGlobalAlt class="w-4 h-4 m-0 mr-2" />
          Sync
        </button>
        <button
          :disabled="!hasInstance"
          @click="handleUpdate"
          :class="
            hasInstance
              ? 'bg-emerald-500 active:bg-emerald-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          class="inline-flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
          type="button"
        >
          Save
          <IconSync class="ml-1 animate-spin" v-if="getSaveLoading" />
        </button>
        <button
          :disabled="!hasInstance || checkCopyExist()"
          @click="handleCopy"
          :class="
            hasInstance && !checkCopyExist()
              ? 'bg-gray-500 active:bg-gray-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          class="inline-flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          Duplicate
          <IconSync class="ml-1 animate-spin" v-if="getCopyLoading" />
        </button>
        <button
          v-if="!categoryNameList.includes(instanceName)"
          :disabled="!hasInstance"
          @click="handleEditName"
          :class="
            hasInstance
              ? 'bg-yellow-500 active:bg-yellow-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          type="button"
          class="flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          <IconPencilAlt class="w-4 h-4 m-0 mr-2" /> Edit name
        </button>
        <button
          :disabled="!hasInstance"
          :class="
            hasInstance
              ? 'bg-yellow-500 active:bg-yellow-600'
              : 'bg-gray-200 active:bg-gray-400'
          "
          @click="copyShortCode"
          type="button"
          class="w-[140px] flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          <IconCode class="w-4 h-4 m-0 mr-2" /> {{ shortCodeText }}
        </button>
        <button
          @click="showPreview"
          type="button"
          class="bg-sky-500 text-white active:bg-sky-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          Live View
        </button>
      </div>
      <!-- *** show draft operation buttons when user select draft edit mode -->
      <div v-else>
        <button
          @click="handleUpdateDraft"
          type="button"
          class="inline-flex bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          Save Draft
          <IconSync class="animate-spin" v-if="getDraftSaveLoading" />
        </button>
        <button
          @click="handleOpenDraftPublishModal"
          type="button"
          class="inline-flex bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          Publish
          <IconSync class="animate-spin" v-if="getSaveLoading" />
        </button>
        <button
          type="button"
          @click="showPreview"
          class="bg-sky-500 text-white active:bg-sky-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        >
          Draft View
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import * as Actions from "@/store/actions.type";
import IconSync from "@/assets/icons/IconSync";
import IconCode from "@/assets/icons/IconCode";
import IconPencilAlt from "@/assets/icons/IconPencilAlt";
import IconGlobalAlt from "@/assets/icons/IconGlobalAlt";
import ToggleBtn from "@/components/Shared/Input/ToggleBtn";
import IconChevronLeft from "@/assets/icons/IconChevronLeft";
import DraftPublishConfirmation from "@/components/Shared/Modal/DraftPublishConfirmation";
import InstanceNameEditModal from "@/components/Shared/Modal/InstanceNameEditModal";
import InstanceSyncModal from "@/components/Shared/Modal/InstanceSyncModal";
import ComponentPreviewModal from "@/components/Shared/Modal/ComponentPreviewModal";

export default {
  name: "DNComponentUpdateHeader",
  components: {
    IconSync,
    IconCode,
    ToggleBtn,
    IconPencilAlt,
    IconGlobalAlt,
    IconChevronLeft,
    InstanceNameEditModal,
    ComponentPreviewModal,
    InstanceSyncModal,
    DraftPublishConfirmation
  },
  data() {
    return {
      showNameEditModal: false,
      showComponentPreviewModal: false,
      showDraftPublicConfirmation: false,
      showInstanceSyncModal: false,
      shortCodeText: "shortcode"
    };
  },
  props: {
    title: {
      type: String,
      required: true
    },
    componentName: {
      type: String,
      required: true
    },
    instanceId: {
      type: String,
      required: true
    },
    draftId: {
      type: String,
      required: true
    },
    handleUpdate: {
      type: Function,
      required: true
    },
    handleCopy: {
      type: Function,
      required: true
    },
    handleUpdateDraft: {
      type: Function,
      required: true
    },
    handleCreateDraft: {
      type: Function,
      required: true
    },
    formData: {
      type: Object,
      required: true
    }
  },
  computed: {
    componentId() {
      let componentName = this.componentName.toLowerCase().replaceAll(" ", "_");
      if (this.getAllComponent.find((item) => item.name === componentName)) {
        return this.getAllComponent.find((item) => item.name === componentName)
          .id;
      } else {
        return "";
      }
    },
    categoryNameList() {
      if (this.categoryInfo.length) {
        return this.categoryInfo.map((item) => item.name);
      } else {
        return [];
      }
    },
    instanceName() {
      if (
        this.getInstanceInfo &&
        this.getInstanceInfo.find((item) => item.id === this.instanceId)
      ) {
        return this.getInstanceInfo
          .find((item) => item.id === this.instanceId)
          .instance_name.split("|")[1];
      } else {
        return null;
      }
    },
    hasInstance() {
      if (this.getInstanceInfo) {
        return !!this.getInstanceInfo.length;
      } else {
        return false;
      }
    },
    hasDraft() {
      if (
        this.getInstanceDraft.find(
          (i) => i.instance_name.split("|")[1] === this.instanceName
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    draftModeStatusLocal: {
      get: function () {
        return this.draftModeStatus;
      },
      set: function (val) {
        this.$store.dispatch(Actions.SET_DRAFT_MODE, { status: val });
      }
    },
    ...mapGetters([
      "getRegion",
      "getSaveLoading",
      "getCopyLoading",
      "getDraftSaveLoading",
      "getDraftCreateLoading",
      "getAllComponent",
      "categoryInfo",
      "getInstanceInfo",
      "getInstanceDraft",
      "draftModeStatus"
    ])
  },
  emits: ["update:instance"],
  methods: {
    formattedTitle(name) {
      if (name) {
        const splitName = name.split("|");
        splitName[1] = splitName[1].toLowerCase();
        if (splitName[1].includes("_") || splitName[1].includes("-")) {
          splitName[1] = splitName[1].replaceAll("_", " ");
          splitName[1] = splitName[1].replaceAll("-", " ");
        }
        return splitName[1];
      } else {
        return "Loading...";
      }
    },
    showPreview() {
      this.handleOpenComponentPreviewModal();
    },
    copyShortCode() {
      navigator.clipboard.writeText(
        `[${this.componentName.toLowerCase().replaceAll(" ", "_")} name=${
          this.componentId
        }|${this.instanceName}]`
      );
      this.shortCodeText = "copied";
      setTimeout(() => {
        this.shortCodeText = "shortcode";
      }, 3000);
    },
    handleOpenNameEditModal() {
      this.showNameEditModal = true;
    },
    handleCloseNameEditModal() {
      this.showNameEditModal = false;
    },
    handleOpenDraftPublishModal() {
      this.showDraftPublicConfirmation = true;
    },
    handleCloseDraftPublishModal() {
      this.showDraftPublicConfirmation = false;
    },
    handleOpenInstanceSyncModal() {
      this.showInstanceSyncModal = true;
    },
    handleCloseInstanceSyncModal() {
      this.showInstanceSyncModal = false;
    },
    handleOpenComponentPreviewModal() {
      this.showComponentPreviewModal = true;
    },
    handleCloseComponentPreviewModal() {
      this.showComponentPreviewModal = false;
    },
    handleEditName() {
      this.handleOpenNameEditModal();
    },
    handleGoBack() {
      this.$router.back();
    },
    handleUpdateStatus(val) {
      this.$store.dispatch(Actions.SET_DRAFT_MODE, { status: val });
    },
    checkCopyExist() {
      let copyExist = false;
      this.getInstanceInfo.forEach((item) => {
        if (item.instance_name === this.title + "_copy") {
          copyExist = true;
        }
      });
      return copyExist;
    },
    handlePublish() {
      this.handleUpdate().then(() => {
        this.handleUpdateDraft();
        this.$store.dispatch(Actions.FETCH_INSTANCE_INFO, {
          region: this.getRegion,
          componentId: this.componentId
        });
        this.$store.dispatch(Actions.SET_DRAFT_MODE, { status: "no" });
      });
    }
  }
};
</script>
